import React, { forwardRef, useState } from 'react';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Box from '@mui/material/Box';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

import cancelIcon from '../assets/icons/cancel.svg';

export type ClavisTextFieldProps = Omit<TextFieldProps, 'InputProps'> & {
  onClear?: () => void;
  clearAriaLabel?: string;
  /**
   * When true on a `type="password"` field, render an eye/eye-off toggle
   * that flips the visible characters between masked and plain. Replaces
   * the clear-X adornment (only one trailing affordance fits per Figma).
   */
  passwordToggle?: boolean;
  showPasswordAriaLabel?: string;
  hidePasswordAriaLabel?: string;
};

const ClavisTextField = forwardRef<HTMLDivElement, ClavisTextFieldProps>(
  function ClavisTextField(
    {
      onClear,
      clearAriaLabel = 'Eingabe löschen',
      passwordToggle,
      showPasswordAriaLabel = 'Passwort anzeigen',
      hidePasswordAriaLabel = 'Passwort verbergen',
      value,
      onChange,
      slotProps,
      sx: callerSx,
      type,
      ...rest
    },
    ref,
  ) {
    const hasValue =
      value !== undefined && value !== null && String(value).length > 0;

    const [showPassword, setShowPassword] = useState(false);
    const isPasswordWithToggle = passwordToggle && type === 'password';
    const effectiveType = isPasswordWithToggle && showPassword ? 'text' : type;

    const handleClear = () => {
      if (onClear) {
        onClear();
        return;
      }
      if (onChange) {
        const synthetic = {
          target: { value: '' },
          currentTarget: { value: '' },
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        onChange(synthetic);
      }
    };

    const endAdornment = isPasswordWithToggle ? (
      <InputAdornment position="end">
        <IconButton
          aria-label={showPassword ? hidePasswordAriaLabel : showPasswordAriaLabel}
          onClick={() => setShowPassword((s) => !s)}
          edge="end"
          size="small"
        >
          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ) : hasValue ? (
      <InputAdornment position="end">
        <IconButton
          aria-label={clearAriaLabel}
          tabIndex={-1}
          onClick={handleClear}
          edge="end"
          size="small"
        >
          {/* Figma: text_field Trailing icon (Icon#58665:2327, slot 63:32111).
             Visual = icons/cancel.svg (node 79:3240, X-im-Kreis). */}
          <Box
            component="img"
            src={cancelIcon}
            alt=""
            aria-hidden
            sx={{ width: '24px', height: '24px', display: 'block' }}
          />
        </IconButton>
      </InputAdornment>
    ) : null;

    // WCAG 2.1 SC 4.1.3 (Status Messages, AA): a validation/submit error must
    // reach assistive tech WITHOUT receiving focus. The helper text carries the
    // message and is already associated via aria-describedby — but on its own it
    // is only spoken when the field is focused, so a screen-reader user who
    // submits a form hears nothing about the failure (found in the 2026-07-11
    // Orca screen-reader pass). Making the helper text a live region while it
    // holds an error announces it the moment it appears.
    const mergedSlotProps = {
      ...(slotProps ?? {}),
      input: {
        ...((slotProps as { input?: Record<string, unknown> })?.input ?? {}),
        endAdornment,
      },
      formHelperText: {
        ...((slotProps as { formHelperText?: Record<string, unknown> })?.formHelperText ?? {}),
        ...(rest.error ? { role: 'alert' as const } : {}),
      },
    };

    const baseSx = {
      // Figma Text field: stroke #605D64@1 (NICHT MUI default rgba(0,0,0,0.23)).
      // Selektor mit zusätzlichem .MuiOutlinedInput-root als Mittel-Klasse,
      // damit Specificity die theme-default `.MuiOutlinedInput-root
      // .MuiOutlinedInput-notchedOutline` (= #CAC4D0) zuverlässig schlägt.
      // borderWidth explizit auf 1px gesetzt für Konsistenz über alle States.
      '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
        borderColor: '#605D64',
        borderWidth: '1px',
      },
      '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#605D64',
        borderWidth: '1px',
      },
      // Figma supporting-text: Roboto 12/400/16 ls=0.4 #605D64.
      // fontFamily explicit weil MUI's caption variant ('Twemoji Country
      // Flags', 'Noto Sans', sans-serif) sonst silent über das gewünschte
      // Roboto cascades (theme-cascade edge case).
      '& .MuiFormHelperText-root': {
        fontFamily: "'Roboto', sans-serif",
        color: '#605D64',
        letterSpacing: '0.4px',
      },
    };

    // Compose sx as an array so caller-supplied function / array sx values
    // survive intact (spreading them into an object would drop functions and
    // turn array entries into numeric keys).
    const composedSx = Array.isArray(callerSx)
      ? [baseSx, ...callerSx]
      : callerSx
        ? [baseSx, callerSx]
        : baseSx;

    return (
      <TextField
        ref={ref}
        value={value}
        onChange={onChange}
        type={effectiveType}
        slotProps={mergedSlotProps}
        sx={composedSx}
        {...rest}
      />
    );
  },
);

export default ClavisTextField;
